import React, { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { CheckCircle, Info } from '@mui/icons-material';
import { CircularProgress, TextField } from '@mui/material';

import * as S from './styles';
import Button from 'components/Button';
import Text from 'components/Text';
import { useThemeContext } from 'global/ThemeContext/ThemeContext';
import { useToast } from 'hooks/useToast';
import GlobalModal from 'modals/GlobalModal/GlobalModal';
import { validateRecoveryCode } from 'services/recoveryCodeService';
import { useIsMobile } from 'utils/mediaQueries';

interface CodeValidationModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (email: string) => void;
  whatsappNumber: string;
}

const codeSchema = yup.object().shape({
  code: yup
    .string()
    .required('Código é obrigatório')
    .matches(/^\d{6}$/, 'Código deve conter 6 dígitos'),
});

const CodeValidationModal: React.FC<CodeValidationModalProps> = ({
  open,
  onClose,
  onSuccess,
  whatsappNumber,
}) => {
  const theme = useThemeContext();
  const { toastError, toastSuccess } = useToast();
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(codeSchema),
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: { code: string }) => {
    setLoading(true);
    try {
      // console.log('[CodeValidation] Validating code:', data.code);

      // Validate code via API
      const response = await validateRecoveryCode(data.code);

      // console.log('[CodeValidation] Validation response:', response);

      if (response.success) {
        // Validation successful
        if (response.email) {
          // Email retrieved successfully
          // console.log('[CodeValidation] Email retrieved:', response.email);
          toastSuccess('Código validado com sucesso!');
          handleClose();
          onSuccess(response.email);
        } else {
          // Valid code but couldn't retrieve email
          // console.warn('[CodeValidation] Valid code but no email returned');
          toastError('Código validado, mas não foi possível recuperar o e-mail. Entre em contato com o suporte.');
        }
      } else {
        // Validation failed
        console.log('[CodeValidation] Validation failed:', response.message);
        toastError('Código inválido ou expirado');
      }
    } catch (error: any) {
      console.error('[CodeValidation] Error:', error);
      toastError(error.message || 'Erro ao validar código. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <GlobalModal
      title="VALIDAÇÃO DE CÓDIGO"
      open={open}
      onClose={handleClose}
      width={isMobile ? '90%' : '500px'}
    >
      <S.Content>
        <S.IconContainer>
          <CheckCircle
            style={{
              fontSize: 48,
              color: theme.primary || '#FFFFFF',
            }}
          />
        </S.IconContainer>

        <S.InfoText>
          <Text size="medium" color="textPrimary">
            Código de recuperação enviado!
          </Text>
          <Text size="small" color="textSecondary">
            Um código de 6 dígitos foi enviado para o WhatsApp {whatsappNumber.slice(0, 3)}
            ****{whatsappNumber.slice(-4)} e para o seu e-mail cadastrado.
          </Text>
        </S.InfoText>

        <S.FormContainer onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="code"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Código de 6 dígitos"
                placeholder="000000"
                fullWidth
                error={!!errors.code}
                helperText={errors.code?.message}
                size={isMobile ? 'small' : 'medium'}
                disabled={loading}
                inputProps={{
                  inputMode: 'numeric',
                  maxLength: 6,
                  pattern: '[0-9]*',
                  style: {
                    fontSize: '24px',
                    letterSpacing: '8px',
                    textAlign: 'center',
                  },
                }}
              />
            )}
          />

          <S.HelpText>
            <Info fontSize="small" style={{ color: theme.warning || '#ff9800' }} />
            <Text size="small" color="textSecondary">
              O código expira em 15 minutos. Verifique seu WhatsApp e e-mail.
            </Text>
          </S.HelpText>

          <S.ButtonContainer>
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={handleClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary" fullWidth disabled={loading}>
              {loading ? (
                <CircularProgress size={20} sx={{ color: theme.primary }} />
              ) : (
                'Validar Código'
              )}
            </Button>
          </S.ButtonContainer>
        </S.FormContainer>

        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <Text size="small" color="textSecondary">
            Não recebeu o código? Aguarde alguns segundos ou tente novamente.
          </Text>
        </div>
      </S.Content>
    </GlobalModal>
  );
};

export default CodeValidationModal;
